/**
 * VGrove 标准化 Loader 工具
 * 提供基于 React Router v6+ loader 模式的标准化认证和权限检查
 * 替代原有的 RouteProtectionWrapper、RuntimeExecutor 等复杂系统
 */

import { redirect, type LoaderFunction, type LoaderFunctionArgs } from 'react-router';
import { debug } from './debug';
import type { BaseUser } from './types';

/**
 * 认证检查函数类型
 */
export type AuthCheck<TUser extends BaseUser = BaseUser> = () => Promise<TUser | null> | TUser | null;

/**
 * 角色检查函数类型
 */
export type RoleCheck<TUser extends BaseUser = BaseUser> = (user: TUser, requiredRoles: string[]) => boolean;

/**
 * 权限检查函数类型
 */
export type PermissionCheck<TUser extends BaseUser = BaseUser> = (user: TUser, requiredPermissions: string[]) => boolean;

/**
 * Loader 配置选项
 */
export interface LoaderOptions<TUser extends BaseUser = BaseUser> {
  /** 认证检查函数 */
  authCheck?: AuthCheck<TUser>;
  /** 角色检查函数 */
  roleCheck?: RoleCheck<TUser>;
  /** 权限检查函数 */
  permissionCheck?: PermissionCheck<TUser>;
  /** 未认证时的重定向路径 */
  loginPath?: string;
  /** 权限不足时的重定向路径 */
  forbiddenPath?: string;
  /** 公共路径列表（无需认证） */
  publicPaths?: string[];
}

/**
 * 默认配置
 */
const DEFAULT_OPTIONS: Required<LoaderOptions> = {
  authCheck: () => null,
  roleCheck: (user, roles) => {
    if (!user.roles) return false;
    return roles.some(role => user.roles!.includes(role));
  },
  permissionCheck: (user, permissions) => {
    if (!user.permissions) return false;
    return permissions.some(permission => user.permissions!.includes(permission));
  },
  loginPath: '/login',
  forbiddenPath: '/403',
  publicPaths: ['/login', '/register', '/forgot-password', '/404', '/403']
};

/**
 * 全局配置
 */
let globalOptions: Partial<LoaderOptions<any>> = {};

/**
 * 配置全局 loader 选项
 */
export function configureLoaders<TUser extends BaseUser = BaseUser>(options: LoaderOptions<TUser>): void {
  globalOptions = { ...globalOptions, ...options } as any;
  debug.auth('Loader 全局配置已更新', options);
}

/**
 * 获取合并后的配置
 */
function getMergedOptions<TUser extends BaseUser = BaseUser>(options?: Partial<LoaderOptions<TUser>>): Required<LoaderOptions<TUser>> {
  return {
    ...DEFAULT_OPTIONS,
    ...globalOptions,
    ...options
  } as Required<LoaderOptions<TUser>>;
}

/**
 * 检查路径是否为公共路径
 */
function isPublicPath(pathname: string, publicPaths: string[]): boolean {
  return publicPaths.some(path => {
    if (path.endsWith('*')) {
      return pathname.startsWith(path.slice(0, -1));
    }
    return pathname === path;
  });
}

/**
 * 创建认证 loader
 * 检查用户是否已认证，未认证则重定向到登录页
 */
export function createAuthLoader<TUser extends BaseUser = BaseUser>(options?: Partial<LoaderOptions<TUser>>): LoaderFunction {
  return async ({ request }: LoaderFunctionArgs) => {
    const config = getMergedOptions<TUser>(options);
    const url = new URL(request.url);

    debug.auth('执行认证检查', { pathname: url.pathname });

    // 检查是否为公共路径
    if (isPublicPath(url.pathname, config.publicPaths)) {
      debug.auth('公共路径，跳过认证检查', { pathname: url.pathname });
      return null;
    }

    // 执行认证检查
    const user = await config.authCheck();

    if (!user) {
      debug.auth('用户未认证，重定向到登录页', {
        pathname: url.pathname,
        loginPath: config.loginPath
      });

      // 保存原始路径用于登录后重定向
      const redirectUrl = new URL(config.loginPath, url.origin);
      redirectUrl.searchParams.set('redirect', url.pathname + url.search);

      throw redirect(redirectUrl.toString());
    }

    debug.auth('认证检查通过', { userId: user.id });
    return { user };
  };
}

/**
 * 创建角色检查 loader
 * 检查用户是否具有所需角色
 */
export function createRoleLoader<TUser extends BaseUser = BaseUser>(
  requiredRoles: string[],
  options?: Partial<LoaderOptions<TUser>>
): LoaderFunction {
  return async ({ request }: LoaderFunctionArgs) => {
    const config = getMergedOptions<TUser>(options);
    const url = new URL(request.url);

    debug.auth('执行角色检查', {
      pathname: url.pathname,
      requiredRoles
    });

    // 检查是否为公共路径
    if (isPublicPath(url.pathname, config.publicPaths)) {
      debug.auth('公共路径，跳过角色检查', { pathname: url.pathname });
      return null;
    }

    // 执行认证检查
    const user = await config.authCheck();

    if (!user) {
      debug.auth('用户未认证，重定向到登录页');
      const redirectUrl = new URL(config.loginPath, url.origin);
      redirectUrl.searchParams.set('redirect', url.pathname + url.search);
      throw redirect(redirectUrl.toString());
    }

    // 检查角色
    const hasRole = config.roleCheck(user, requiredRoles);

    if (!hasRole) {
      debug.auth('角色检查失败，重定向到禁止访问页', {
        userId: user.id,
        userRoles: user.roles,
        requiredRoles
      });
      throw redirect(config.forbiddenPath);
    }

    debug.auth('角色检查通过', {
      userId: user.id,
      userRoles: user.roles,
      requiredRoles
    });

    return { user };
  };
}

/**
 * 创建权限检查 loader
 * 检查用户是否具有所需权限
 */
export function createPermissionLoader<TUser extends BaseUser = BaseUser>(
  requiredPermissions: string[],
  options?: Partial<LoaderOptions<TUser>>
): LoaderFunction {
  return async ({ request }: LoaderFunctionArgs) => {
    const config = getMergedOptions<TUser>(options);
    const url = new URL(request.url);

    debug.auth('执行权限检查', {
      pathname: url.pathname,
      requiredPermissions
    });

    // 检查是否为公共路径
    if (isPublicPath(url.pathname, config.publicPaths)) {
      debug.auth('公共路径，跳过权限检查', { pathname: url.pathname });
      return null;
    }

    // 执行认证检查
    const user = await config.authCheck();

    if (!user) {
      debug.auth('用户未认证，重定向到登录页');
      const redirectUrl = new URL(config.loginPath, url.origin);
      redirectUrl.searchParams.set('redirect', url.pathname + url.search);
      throw redirect(redirectUrl.toString());
    }

    // 检查权限
    const hasPermission = config.permissionCheck(user, requiredPermissions);

    if (!hasPermission) {
      debug.auth('权限检查失败，重定向到禁止访问页', {
        userId: user.id,
        userPermissions: user.permissions,
        requiredPermissions
      });
      throw redirect(config.forbiddenPath);
    }

    debug.auth('权限检查通过', {
      userId: user.id,
      userPermissions: user.permissions,
      requiredPermissions
    });

    return { user };
  };
}

/**
 * 创建组合 loader
 * 同时检查认证、角色和权限
 */
export function createCombinedLoader<TUser extends BaseUser = BaseUser>(
  config: {
    roles?: string[];
    permissions?: string[];
    requireAll?: boolean; // 是否需要同时满足所有条件
  },
  options?: Partial<LoaderOptions<TUser>>
): LoaderFunction {
  return async ({ request }: LoaderFunctionArgs) => {
    const loaderConfig = getMergedOptions<TUser>(options);
    const url = new URL(request.url);
    const { roles = [], permissions = [], requireAll = false } = config;

    debug.auth('执行组合检查', {
      pathname: url.pathname,
      roles,
      permissions,
      requireAll
    });

    // 检查是否为公共路径
    if (isPublicPath(url.pathname, loaderConfig.publicPaths)) {
      debug.auth('公共路径，跳过组合检查', { pathname: url.pathname });
      return null;
    }

    // 执行认证检查
    const user = await loaderConfig.authCheck();

    if (!user) {
      debug.auth('用户未认证，重定向到登录页');
      const redirectUrl = new URL(loaderConfig.loginPath, url.origin);
      redirectUrl.searchParams.set('redirect', url.pathname + url.search);
      throw redirect(redirectUrl.toString());
    }

    // 检查角色和权限
    const hasRole = roles.length === 0 || loaderConfig.roleCheck(user, roles);
    const hasPermission = permissions.length === 0 || loaderConfig.permissionCheck(user, permissions);

    let accessGranted: boolean;

    if (requireAll) {
      // 需要同时满足角色和权限
      accessGranted = hasRole && hasPermission;
    } else {
      // 满足角色或权限之一即可
      accessGranted = hasRole || hasPermission;
    }

    if (!accessGranted) {
      debug.auth('组合检查失败，重定向到禁止访问页', {
        userId: user.id,
        hasRole,
        hasPermission,
        requireAll
      });
      throw redirect(loaderConfig.forbiddenPath);
    }

    debug.auth('组合检查通过', {
      userId: user.id,
      hasRole,
      hasPermission
    });

    return { user };
  };
}

/**
 * 创建自定义 loader
 * 允许用户定义自己的检查逻辑
 */
export function createCustomLoader<TUser extends BaseUser = BaseUser>(
  checkFunction: (user: TUser, request: Request) => Promise<boolean> | boolean,
  options?: Partial<LoaderOptions<TUser>>
): LoaderFunction {
  return async ({ request }: LoaderFunctionArgs) => {
    const config = getMergedOptions<TUser>(options);
    const url = new URL(request.url);

    debug.auth('执行自定义检查', { pathname: url.pathname });

    // 检查是否为公共路径
    if (isPublicPath(url.pathname, config.publicPaths)) {
      debug.auth('公共路径，跳过自定义检查', { pathname: url.pathname });
      return null;
    }

    // 执行认证检查
    const user = await config.authCheck();

    // 执行自定义检查
    const checkResult = await checkFunction(user!, request);

    if (!checkResult) {
      debug.auth('自定义检查失败');

      if (!user) {
        // 用户未认证
        const redirectUrl = new URL(config.loginPath, url.origin);
        redirectUrl.searchParams.set('redirect', url.pathname + url.search);
        throw redirect(redirectUrl.toString());
      } else {
        // 用户已认证但检查失败
        throw redirect(config.forbiddenPath);
      }
    }

    debug.auth('自定义检查通过', { userId: user?.id });
    return { user };
  };
}

/**
 * 组合多个 loader
 * 按顺序执行多个 loader，任何一个失败都会中断
 */
export function combineLoaders(...loaders: LoaderFunction[]): LoaderFunction {
  return async (args: LoaderFunctionArgs) => {
    debug.auth('执行组合 loader', { count: loaders.length });

    let combinedResult: any = {};

    for (let i = 0; i < loaders.length; i++) {
      const loader = loaders[i];
      const result = await loader(args);

      if (result) {
        combinedResult = { ...combinedResult, ...result };
      }
    }

    debug.auth('组合 loader 执行完成');
    return Object.keys(combinedResult).length > 0 ? combinedResult : null;
  };
}